/**
 * Mock de dados do BankingProduct (product_catalog) para prototipação da tela de
 * cadastro de produtos no módulo Deals.
 *
 * Estrutura alinhada com a modelagem registrada em:
 *   product_catalog/docs/context/business-context.md
 *
 * Escopo desta rodada: BankingProduct + EligibilityCriteria + TemplateBinding (stage) +
 * AllowedDealParticipant/DealRequiredDocument (dentro do Deal) + B3CustodyConfig (1:1).
 * Tudo mock — nenhum destes tipos existe ainda como API real.
 */

// ===================== enums fechados (espelham o domínio) =====================

export type ResultingAssetType = 'RECEIVABLE_ASSIGNMENT' | 'LOAN';

export type ProductStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';

export type RatingGrade = 'AA' | 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

export const RATING_ORDER: RatingGrade[] = ['AA', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

// Tipos de participante — espelham credit.participant_type (contracts_management).
// Referenciados por id, nunca duplicados como cadastro próprio do catálogo.
export type ParticipantTypeId = 1 | 2 | 3 | 4 | 5 | 6;

export const PARTICIPANT_TYPES: { id: ParticipantTypeId; name: string; label: string }[] = [
    { id: 1, name: 'BORROWER', label: 'Tomador (Borrower)' },
    { id: 2, name: 'GUARANTOR', label: 'Avalista/Fiador (Guarantor)' },
    { id: 3, name: 'ASSIGNOR', label: 'Cedente (Assignor)' },
    { id: 4, name: 'DRAWEE', label: 'Sacado (Drawee)' },
    { id: 5, name: 'GUARANTOR-RETAIL', label: 'Avalista/Fiador Revenda' },
    { id: 6, name: 'RECIPIENT', label: 'Recebedor do desembolso (Recipient)' },
];

export type DealStage = 'STRUCTURING' | 'CREDIT_ANALYSIS' | 'CONTRACT_GENERATION' | 'AMENDMENT';

export const DEAL_STAGES: { value: DealStage; label: string }[] = [
    { value: 'STRUCTURING', label: 'Estruturação' },
    { value: 'CREDIT_ANALYSIS', label: 'Análise de Crédito' },
    { value: 'CONTRACT_GENERATION', label: 'Geração de Contrato' },
    { value: 'AMENDMENT', label: 'Aditamento' },
];

// Tipo de documento de identificação exigido do participante (person.Document) —
// lista aberta a expandir; hoje cobre o que foi observado nos contratos reais.
export type RequiredDocumentType =
    | 'RG_CNH' | 'CPF_CNPJ_CARD' | 'PROOF_OF_ADDRESS' | 'FINANCIAL_STATEMENT'
    | 'CONTRACT_SOCIAL' | 'BALANCE_SHEET' | 'INCOME_TAX_RETURN';

export const REQUIRED_DOCUMENT_TYPES: { value: RequiredDocumentType; label: string }[] = [
    { value: 'RG_CNH', label: 'RG ou CNH' },
    { value: 'CPF_CNPJ_CARD', label: 'Cartão CPF/CNPJ' },
    { value: 'PROOF_OF_ADDRESS', label: 'Comprovante de endereço' },
    { value: 'FINANCIAL_STATEMENT', label: 'Declaração de faturamento' },
    { value: 'CONTRACT_SOCIAL', label: 'Contrato social / última alteração' },
    { value: 'BALANCE_SHEET', label: 'Balanço patrimonial' },
    { value: 'INCOME_TAX_RETURN', label: 'IRPF dos sócios' },
];

// Tipo de garantia — 11 tipos reais do schema B3 (CprPost.collaterals[].collateralTypeCode)
// + CDB_VINCULADO (garantia financeira interna, fora do padrão B3 — ver ponto aberto no
// business-context.md: fronteira collateral vs. instrumento financeiro ainda não decidida).
export type GuaranteeType =
    | 'AVAL_PROPRIO' | 'SEM_GARANTIA' | 'AVAL_TERCEIROS' | 'SEGURO' | 'HIPOTECA'
    | 'FIANCA' | 'PENHOR' | 'ALIENACAO_FIDUCIARIA' | 'CESSAO_FIDUCIARIA' | 'OUTROS'
    | 'CPR_GARANTIA' | 'CDB_VINCULADO';

export const GUARANTEE_TYPES: { value: GuaranteeType; label: string; b3Code: string | null }[] = [
    { value: 'AVAL_PROPRIO', label: 'Aval Próprio', b3Code: '0' },
    { value: 'SEM_GARANTIA', label: 'Sem Garantia', b3Code: '1' },
    { value: 'AVAL_TERCEIROS', label: 'Aval de Terceiros', b3Code: '2' },
    { value: 'SEGURO', label: 'Seguro', b3Code: '3' },
    { value: 'HIPOTECA', label: 'Hipoteca', b3Code: '4' },
    { value: 'FIANCA', label: 'Fiança', b3Code: '5' },
    { value: 'PENHOR', label: 'Penhor', b3Code: '6' },
    { value: 'ALIENACAO_FIDUCIARIA', label: 'Alienação Fiduciária', b3Code: '7' },
    { value: 'CESSAO_FIDUCIARIA', label: 'Cessão Fiduciária', b3Code: '8' },
    { value: 'OUTROS', label: 'Outros', b3Code: '9' },
    { value: 'CPR_GARANTIA', label: 'CPR Garantia (Filha)', b3Code: '10' },
    { value: 'CDB_VINCULADO', label: 'CDB Vinculado (garantia financeira interna)', b3Code: null },
];

// Produto da B3 (linguagem da B3): CPR-instrument vs. credit-right/LCA.
// "Não registra na B3" NÃO é um valor aqui — é a AUSÊNCIA de config (b3Config === null).
// Ver b3_custody/docs/decisions/ADR-004.
export type RegistrationType = 'CPR_INSTRUMENT' | 'CREDIT_RIGHT';

export const REGISTRATION_TYPES: { value: RegistrationType; label: string }[] = [
    { value: 'CPR_INSTRUMENT', label: 'CPR (fluxo b3_custody /cpr)' },
    { value: 'CREDIT_RIGHT', label: 'LCA Credit Right (fluxo b3_custody /lca)' },
];

// ===================== configuração de registro B3 (b3_custody) =====================
// Modelo 1:1: cada produto de crédito que registra na B3 tem UMA config própria embutida
// (CustodyProduct no b3_custody). Não há catálogo de perfis reutilizáveis.
// Só entram aqui os campos que o USUÁRIO configura (Cat.2 identidade Agrolend + Cat.3
// regra de garantia). Os fixos técnicos (Cat.1) permanecem no SSM; os dados da operação
// (Cat.4) vêm no evento CREDIT_OPERATION_BILLED. Ver ADR-004 e b3-payload-structure.md.

export interface B3CustodyConfig {
    b3Product: RegistrationType; // CPR_INSTRUMENT | CREDIT_RIGHT

    // --- Cat.3: o que VARIA por produto (único destino de tela) ---
    registersGuarantee: boolean;    // se false, registra como "Sem Garantia" (B3 exige o bloco)
    collateralTypeCode?: string;    // tipo de garantia B3 (0-10), quando registersGuarantee
    operationModalityCode?: string; // modalidade SCR/BACEN — varia por tipo de operação
    finalityCode?: string;          // finalidade SCR — varia por produto
    ballastTypeCode?: string;       // LCA / CDCA — decisão de lastro por produto
}

// ===================== perfis de registro ERP/Dimensa (erp) =====================
// Espelha os parâmetros hoje hardcoded em settlement/_lambda/common/domain.py e
// dimensa_request_service.py (siglaModalidade, códigos institucionais). Placeholders
// a validar contra o serviço erp — ver ponto aberto no business-context.md.

export type DimensaRegistrationType = 'DIMENSA' | 'NONE';

export const DIMENSA_REGISTRATION_TYPES: { value: DimensaRegistrationType; label: string }[] = [
    { value: 'DIMENSA', label: 'Registra na Dimensa (ERP)' },
    { value: 'NONE', label: 'Não registra na Dimensa' },
];

export interface DimensaRegistrationProfile {
    profileId: string;
    profileName: string;
    registrationType: DimensaRegistrationType;
    siglaModalidade?: string;      // ex.: EM03F365, DESSCOOB
    codigoCarteira?: string;       // carteira contábil
    codigoProdutoDimensa?: string; // código do produto no ERP
    tipoOperacao?: string;         // classificação da operação no ERP
}

export const dimensaRegistrationProfiles: DimensaRegistrationProfile[] = [
    {
        profileId: 'dmprof-001', profileName: 'Cessão sem Regresso (padrão)',
        registrationType: 'DIMENSA', siglaModalidade: 'DESSCOOB', codigoCarteira: '01',
        codigoProdutoDimensa: 'CESSAO', tipoOperacao: 'CESSION_WITHOUT_RETURN',
    },
    {
        profileId: 'dmprof-002', profileName: 'CPR-F / Empréstimo PJ',
        registrationType: 'DIMENSA', siglaModalidade: 'EM03F365', codigoCarteira: '02',
        codigoProdutoDimensa: 'EMPR_PJ', tipoOperacao: 'COMMON_CPRF',
    },
    {
        profileId: 'dmprof-003', profileName: 'Não se aplica (não registra na Dimensa)',
        registrationType: 'NONE',
    },
];

// ===================== TemplateBinding (contract, por stage) =====================

// ===================== Instrumento jurídico (contract) — READ-ONLY no produto =====================
// O jurídico é dono dos instrumentos/templates. O produto apenas SELECIONA de um catálogo
// de instrumentos ATIVOS/LIBERADOS. Ver ADR-001 do product_catalog.
// Este catálogo simula o que viria do serviço `contract` (hoje mock).

export interface LegalInstrument {
    instrumentCode: string;   // ContractType — ex.: CPR_F, ASSIGNMENT_CONTRACT
    name: string;             // nome do instrumento jurídico
    status: 'ACTIVE' | 'INACTIVE'; // só ACTIVE é selecionável
    released: boolean;        // "liberado" pelo jurídico (versão vigente APPROVED)
}

export const legalInstruments: LegalInstrument[] = [
    { instrumentCode: 'ASSIGNMENT_CONTRACT', name: 'Contrato de Cessão de Direitos Creditórios', status: 'ACTIVE', released: true },
    { instrumentCode: 'ASSIGNMENT_AMENDMENT', name: 'Aditamento de Cessão', status: 'ACTIVE', released: true },
    { instrumentCode: 'ASSIGNMENT_USD', name: 'Contrato de Cessão em Moeda Estrangeira (USD)', status: 'ACTIVE', released: true },
    { instrumentCode: 'CPR_F', name: 'CPR-F', status: 'ACTIVE', released: true },
    { instrumentCode: 'CPR_F_THIRD_PARTY_AVAL', name: 'CPR-F com Aval de Terceiro', status: 'ACTIVE', released: true },
    { instrumentCode: 'CPR_FID_CONTRACT', name: 'CPR + Cessão Fiduciária de Contrato', status: 'ACTIVE', released: true },
    { instrumentCode: 'CPR_CLEAN_ORDERS', name: 'CPR Clean com Pedidos em Anexo', status: 'ACTIVE', released: true },
    { instrumentCode: 'CPR_CDB', name: 'CPR com CDB em Garantia', status: 'ACTIVE', released: true },
    { instrumentCode: 'ASSIGNMENT_BONUS', name: 'Contrato de Cessão com Bônus de Adimplência', status: 'ACTIVE', released: true },
    { instrumentCode: 'LEGACY_MODEL', name: 'Modelo Legado (descontinuado)', status: 'INACTIVE', released: false },
];

// Vínculo produto → instrumento por stage (e opcionalmente por cliente, via cascata do
// TemplateBinding real). O produto SELECIONA o instrumento; não cria.
export interface InstrumentBinding {
    bindingId: string;
    stage: DealStage;
    instrumentCode: string;   // referência a LegalInstrument.instrumentCode (seleção)
    customerId?: string;      // opcional — override por cliente (cascata)
}

export const getInstrumentName = (code: string): string =>
    legalInstruments.find(i => i.instrumentCode === code)?.name || code;

// ===================== EligibilityCriteria (por papel) =====================

export interface EligibilityCriteria {
    criteriaId: string;
    participantTypeId: ParticipantTypeId;
    minRating: RatingGrade | null; // null = "Todos" (sem piso, ex.: CORE do lado cedente)
    maxRating: RatingGrade | null;
}

// ===================== AllowedDealParticipant + DealRequiredDocument (Deal) =====================

export interface DealRequiredDocument {
    documentType: RequiredDocumentType;
    isRequired: boolean;
}

export interface AllowedDealParticipant {
    allowedParticipantId: string;
    participantTypeId: ParticipantTypeId;
    isRequired: boolean;
    requiredDocuments: DealRequiredDocument[];
}

// ===================== estrutura de operação (planilha técnica — 6 eixos) =====================
// Espelha a matriz de produtos da planilha da mesa. Ver business-context.md
// "Estrutura real de operação".

export type InstrumentType =
    | 'RECEIVABLES_ASSIGNMENT'      // Cessão de recebíveis
    | 'CONTRACT_ASSIGNMENT'         // Cessão de contrato
    | 'CPR'                         // CPR pura
    | 'CPR_FID_CONTRACT'            // CPR + Cessão Fiduciária de Contrato
    | 'CPR_FID_DUPLICATES'          // CPR + Cessão Fiduciária de Duplicatas
    | 'CPR_FID_ORDER'               // CPR + Cessão Fiduciária de Pedido
    | 'ORDER_ASSIGNMENT'           // Cessão de pedido
    | 'ADVANCE_INSTRUMENT_CDB';     // CPR / Instrumento de adiantamento + CDB em garantia

export const INSTRUMENT_TYPES: { value: InstrumentType; label: string }[] = [
    { value: 'RECEIVABLES_ASSIGNMENT', label: 'Cessão de recebíveis' },
    { value: 'CONTRACT_ASSIGNMENT', label: 'Cessão de contrato' },
    { value: 'CPR', label: 'CPR' },
    { value: 'CPR_FID_CONTRACT', label: 'CPR + Cessão Fid. de Contrato' },
    { value: 'CPR_FID_DUPLICATES', label: 'CPR + Cessão Fid. de Duplicatas' },
    { value: 'CPR_FID_ORDER', label: 'CPR + Cessão Fid. de Pedido' },
    { value: 'ORDER_ASSIGNMENT', label: 'Cessão de pedido' },
    { value: 'ADVANCE_INSTRUMENT_CDB', label: 'CPR / Adiantamento + CDB em garantia' },
];

// Regra de compatibilidade instrumento → fluxos B3 permitidos.
// É uma VERDADE do domínio (cessão de recebíveis não gera CPR), não decisão de UI.
// A tela usa isto só para filtrar; a invariante real deve viver no product_catalog.
// "Não registra na B3" não é um valor aqui — é a ausência de b3Config no produto.
export const INSTRUMENT_TO_REGISTRATION_TYPES: Record<InstrumentType, RegistrationType[]> = {
    RECEIVABLES_ASSIGNMENT: ['CREDIT_RIGHT'],
    CONTRACT_ASSIGNMENT: ['CREDIT_RIGHT'],
    ORDER_ASSIGNMENT: ['CPR_INSTRUMENT'],
    CPR: ['CPR_INSTRUMENT'],
    CPR_FID_CONTRACT: ['CPR_INSTRUMENT'],
    CPR_FID_DUPLICATES: ['CPR_INSTRUMENT'],
    CPR_FID_ORDER: ['CPR_INSTRUMENT'],
    ADVANCE_INSTRUMENT_CDB: ['CPR_INSTRUMENT'],
};

// Lastro que é descontado (recebível-fonte).
export type DiscountedReceivableType = 'NF' | 'CONTRACT' | 'ORDER' | 'NONE';

export const DISCOUNTED_RECEIVABLE_TYPES: { value: DiscountedReceivableType; label: string }[] = [
    { value: 'NF', label: 'N sacados (NF)' },
    { value: 'CONTRACT', label: 'N contratos' },
    { value: 'ORDER', label: 'N pedidos' },
    { value: 'NONE', label: 'N/A' },
];

// Sim / Não / N/A — usado para regresso do recebível e da garantia.
export type TernaryFlag = 'YES' | 'NO' | 'NA';

export const TERNARY_OPTIONS: { value: TernaryFlag; label: string }[] = [
    { value: 'YES', label: 'Sim' },
    { value: 'NO', label: 'Não' },
    { value: 'NA', label: 'N/A' },
];

// Granularidade da fatura.
export type InvoiceGranularity =
    | 'PER_RECEIVABLE'          // 1 por recebível
    | 'PER_CONTRACT_INSTALLMENT'// 1 por parcela dos contratos
    | 'PER_CPR_INSTALLMENT'     // 1 por parcela da CPR
    | 'CPR_CLEAN_WITH_ORDERS';  // CPR clean com pedidos em anexo (ORDER)

export const INVOICE_GRANULARITY_OPTIONS: { value: InvoiceGranularity; label: string }[] = [
    { value: 'PER_RECEIVABLE', label: '1 por recebível' },
    { value: 'PER_CONTRACT_INSTALLMENT', label: '1 por parcela dos contratos' },
    { value: 'PER_CPR_INSTALLMENT', label: '1 por parcela da CPR' },
    { value: 'CPR_CLEAN_WITH_ORDERS', label: 'CPR clean com pedidos em anexo' },
];

export interface OperationStructure {
    instrumentType: InstrumentType;
    discountedReceivable: DiscountedReceivableType;
    receivableRecourse: TernaryFlag;      // regresso do recebível
    mandatoryGuaranteeLabel: string;      // ex.: "CDB", "Duplicatas", "Contrato", "Ao menos 1 de qualquer tipo", "N/A"
    guaranteeRecourse: TernaryFlag;       // regresso da garantia
    invoiceGranularity: InvoiceGranularity;
    isLcaBallast: boolean;                // flag se é lastro para LCA (camada Operações-Geral)
}

// ===================== BankingProduct (agregado raiz) =====================

export interface BankingProductGuarantee {
    type: GuaranteeType;
    requirement: 'MANDATORY' | 'OPTIONAL';
    retentionPercentage?: number; // específico de CDB_VINCULADO (ex.: 100, 20-30)
}

export interface BankingProduct {
    productId: string;
    commercialName: string;
    shortCode: string; // CORE, CORE+, CROSS, SHIELD, FLOW...
    fullName?: string; // nome por extenso (ex.: "Capital Optimization via Receivables Exchange")
    icon?: string; // emoji exibido no card (estilo da apresentação comercial Agrolend)
    description: string;
    resultingAssetType: ResultingAssetType;
    status: ProductStatus;
    currency: 'BRL' | 'USD';
    isNew?: boolean;

    minRate: number;
    maxRate: number;
    maxAmount: number;
    maxTerm: number;

    guarantees: BankingProductGuarantee[];
    eligibilityCriteria: EligibilityCriteria[];
    allowedParticipants: AllowedDealParticipant[];
    instrumentBindings: InstrumentBinding[];
    operationStructure?: OperationStructure;
    b3Config: B3CustodyConfig | null;
    dimensaRegistrationProfileId: string | null;

    createdBy: string;
    createdAt: string;
    activatedBy?: string;
    activatedAt?: string;
}

// ===================== dados mock — 5 produtos reais + 5 legados =====================

export const bankingProducts: BankingProduct[] = [
    {
        productId: 'bp-core', commercialName: 'CORE', shortCode: 'CORE',
        fullName: 'Capital Optimization via Receivables Exchange', icon: '🥇',
        description: 'Cessão de recebíveis sem regresso — venda definitiva, contas a receber convertidas em caixa. Off-Balance, redução de endividamento, abertura de limite de crédito.',
        resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'ACTIVE', currency: 'BRL',
        minRate: 1.5, maxRate: 3.5, maxAmount: 94000000, maxTerm: 360,
        guarantees: [{ type: 'CESSAO_FIDUCIARIA', requirement: 'MANDATORY' }],
        eligibilityCriteria: [
            { criteriaId: 'ec-core-1', participantTypeId: 3, minRating: null, maxRating: null },
            { criteriaId: 'ec-core-2', participantTypeId: 4, minRating: 'AA', maxRating: 'B' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-core-1', participantTypeId: 3, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
                { documentType: 'FINANCIAL_STATEMENT', isRequired: true },
            ] },
            { allowedParticipantId: 'adp-core-2', participantTypeId: 4, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-core-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'ASSIGNMENT_CONTRACT' },
            { bindingId: 'tb-core-2', stage: 'AMENDMENT', instrumentCode: 'ASSIGNMENT_AMENDMENT' },
        ],
        operationStructure: {
            instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'NO',
            mandatoryGuaranteeLabel: 'N/A', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: true,
        },
        b3Config: { b3Product: 'CREDIT_RIGHT', registersGuarantee: false, operationModalityCode: '', finalityCode: '' },
        dimensaRegistrationProfileId: 'dmprof-001',
        createdBy: 'jean.dobre', createdAt: '2026-01-10T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-01-12T09:00:00Z',
    },
    {
        productId: 'bp-core-plus', commercialName: 'CORE+', shortCode: 'CORE+',
        fullName: 'Capital Optimization via Receivables Exchange +', icon: '📜',
        description: 'Cessão de recebíveis "com regresso" sintético — troca de CR por aplicação financeira (CDB-V 100%), não por caixa. Off-Balance, simples e bilateral.',
        resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'ACTIVE', currency: 'BRL', isNew: true,
        minRate: 1.2, maxRate: 3.0, maxAmount: 50000000, maxTerm: 360,
        guarantees: [{ type: 'CDB_VINCULADO', requirement: 'MANDATORY', retentionPercentage: 100 }],
        eligibilityCriteria: [
            { criteriaId: 'ec-corep-1', participantTypeId: 3, minRating: 'AA', maxRating: 'B' },
            { criteriaId: 'ec-corep-2', participantTypeId: 4, minRating: 'C', maxRating: 'E' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-corep-1', participantTypeId: 3, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
                { documentType: 'BALANCE_SHEET', isRequired: true },
            ] },
            { allowedParticipantId: 'adp-corep-2', participantTypeId: 4, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-corep-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'ASSIGNMENT_CONTRACT' },
        ],
        operationStructure: {
            instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'NO',
            mandatoryGuaranteeLabel: 'CDB', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: true,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: false, collateralTypeCode: '1', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-001',
        createdBy: 'jean.dobre', createdAt: '2026-05-02T10:00:00Z',
    },
    {
        productId: 'bp-cross', commercialName: 'CROSS', shortCode: 'CROSS',
        fullName: 'Cross-currency Receivables Operating Structure', icon: '🔗',
        description: 'Cessão sem regresso em dólar — venda definitiva de recebíveis em USD. Pioneirismo no mercado, abertura de limite de crédito.',
        resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'ACTIVE', currency: 'USD',
        minRate: 1.0, maxRate: 2.5, maxAmount: 20000000, maxTerm: 360,
        guarantees: [{ type: 'CESSAO_FIDUCIARIA', requirement: 'MANDATORY' }],
        eligibilityCriteria: [
            { criteriaId: 'ec-cross-1', participantTypeId: 3, minRating: 'AA', maxRating: 'A' },
            { criteriaId: 'ec-cross-2', participantTypeId: 4, minRating: 'AA', maxRating: 'A' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-cross-1', participantTypeId: 3, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
                { documentType: 'BALANCE_SHEET', isRequired: true },
            ] },
            { allowedParticipantId: 'adp-cross-2', participantTypeId: 4, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-cross-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'ASSIGNMENT_USD' },
        ],
        operationStructure: {
            instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'NO',
            mandatoryGuaranteeLabel: 'N/A', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: true,
        },
        b3Config: { b3Product: 'CREDIT_RIGHT', registersGuarantee: false, operationModalityCode: '', finalityCode: '' },
        dimensaRegistrationProfileId: 'dmprof-001',
        createdBy: 'jean.dobre', createdAt: '2026-02-15T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-02-20T09:00:00Z',
    },
    {
        productId: 'bp-shield', commercialName: 'SHIELD', shortCode: 'SHIELD',
        fullName: 'Structured Hedge via Industry Endorsed Liability Deal', icon: '🏭',
        description: 'CPR-F com aval da indústria — sacado em situação delicada emite CPR-F com aval de terceiro (indústria/fornecedor), desembolso no cliente. Troca de PDD por aval no balanço.',
        resultingAssetType: 'LOAN', status: 'ACTIVE', currency: 'BRL',
        minRate: 1.5, maxRate: 3.0, maxAmount: 80000000, maxTerm: 540,
        guarantees: [{ type: 'AVAL_TERCEIROS', requirement: 'MANDATORY' }],
        eligibilityCriteria: [
            { criteriaId: 'ec-shield-1', participantTypeId: 3, minRating: 'AA', maxRating: 'B' },
            { criteriaId: 'ec-shield-2', participantTypeId: 4, minRating: 'C', maxRating: 'E' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-shield-1', participantTypeId: 1, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
                { documentType: 'RG_CNH', isRequired: true },
            ] },
            { allowedParticipantId: 'adp-shield-2', participantTypeId: 2, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-shield-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'CPR_F_THIRD_PARTY_AVAL' },
        ],
        operationStructure: {
            instrumentType: 'CPR', discountedReceivable: 'NONE', receivableRecourse: 'NA',
            mandatoryGuaranteeLabel: 'Aval de Terceiros (indústria)', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_CPR_INSTALLMENT', isLcaBallast: false,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: true, collateralTypeCode: '2', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-002',
        createdBy: 'jean.dobre', createdAt: '2026-03-01T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-03-05T09:00:00Z',
    },
    {
        productId: 'bp-flow', commercialName: 'FLOW', shortCode: 'FLOW',
        fullName: 'Fast Liquidity Operations Workflow', icon: '💧',
        description: 'Programa revolvente de compra de recebíveis com risk sharing via CDB-Vinculado (20-30%). Off-Balance, saída parcial do risco, estrutura FIDC simples e bilateral. ATENÇÃO: estrutura de "programa revolvente" ainda não confirmada com o comercial — ver ponto aberto no business-context.md.',
        resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'DRAFT', currency: 'BRL',
        minRate: 1.0, maxRate: 2.5, maxAmount: 100000000, maxTerm: 360,
        guarantees: [{ type: 'CDB_VINCULADO', requirement: 'MANDATORY', retentionPercentage: 25 }],
        eligibilityCriteria: [
            { criteriaId: 'ec-flow-1', participantTypeId: 3, minRating: 'AA', maxRating: 'B' },
            { criteriaId: 'ec-flow-2', participantTypeId: 4, minRating: 'C', maxRating: 'E' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-flow-1', participantTypeId: 3, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
                { documentType: 'BALANCE_SHEET', isRequired: true },
            ] },
        ],
        instrumentBindings: [],
        operationStructure: {
            instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'NO',
            mandatoryGuaranteeLabel: 'CDB (20-30%)', guaranteeRecourse: 'YES', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: true,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: false, collateralTypeCode: '1', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-001',
        createdBy: 'jean.dobre', createdAt: '2026-06-01T10:00:00Z',
    },
    {
        productId: 'bp-keep', commercialName: 'KEEP', shortCode: 'KEEP',
        fullName: 'Cessão com Regresso', icon: '🔀',
        description: 'Cessão de recebíveis com regresso — o cedente responde pela solvência. 1 fatura por recebível.',
        resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'ACTIVE', currency: 'BRL',
        minRate: 1.2, maxRate: 3.0, maxAmount: 50000000, maxTerm: 360,
        guarantees: [{ type: 'CESSAO_FIDUCIARIA', requirement: 'MANDATORY' }, { type: 'AVAL_PROPRIO', requirement: 'MANDATORY' }],
        eligibilityCriteria: [
            { criteriaId: 'ec-keep-1', participantTypeId: 3, minRating: 'AA', maxRating: 'C' },
            { criteriaId: 'ec-keep-2', participantTypeId: 4, minRating: 'AA', maxRating: 'E' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-keep-1', participantTypeId: 3, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
                { documentType: 'FINANCIAL_STATEMENT', isRequired: true },
            ] },
            { allowedParticipantId: 'adp-keep-2', participantTypeId: 4, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-keep-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'ASSIGNMENT_CONTRACT' },
        ],
        operationStructure: {
            instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'YES',
            mandatoryGuaranteeLabel: 'N/A', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: true,
        },
        b3Config: { b3Product: 'CREDIT_RIGHT', registersGuarantee: false, operationModalityCode: '', finalityCode: '' },
        dimensaRegistrationProfileId: 'dmprof-001',
        createdBy: 'jean.dobre', createdAt: '2025-11-01T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2025-11-05T09:00:00Z',
    },
    {
        productId: 'bp-pact', commercialName: 'PACT', shortCode: 'PACT',
        fullName: 'CPR + Cessão Fiduciária de Contrato', icon: '📝',
        description: 'CPR + Cessão Fiduciária de Contrato. Garantia é o contrato (pode ser N). A CPR é espelho do contrato: mantém os valores das parcelas, as datas podem ser alteradas. Regresso da garantia: sim.',
        resultingAssetType: 'LOAN', status: 'ACTIVE', currency: 'BRL',
        minRate: 1.3, maxRate: 3.2, maxAmount: 60000000, maxTerm: 540,
        guarantees: [{ type: 'CESSAO_FIDUCIARIA', requirement: 'MANDATORY' }],
        eligibilityCriteria: [
            { criteriaId: 'ec-pact-1', participantTypeId: 1, minRating: 'AA', maxRating: 'C' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-pact-1', participantTypeId: 1, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-pact-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'CPR_FID_CONTRACT' },
        ],
        operationStructure: {
            instrumentType: 'CPR_FID_CONTRACT', discountedReceivable: 'NONE', receivableRecourse: 'NA',
            mandatoryGuaranteeLabel: 'Contrato (pode ser N)', guaranteeRecourse: 'YES', invoiceGranularity: 'PER_CPR_INSTALLMENT', isLcaBallast: false,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: true, collateralTypeCode: '2', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-002',
        createdBy: 'jean.dobre', createdAt: '2026-04-01T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-04-04T09:00:00Z',
    },
    {
        productId: 'bp-cash-collateral', commercialName: 'CASHCOL', shortCode: 'CASHCOL',
        fullName: 'Cash Collateral — CPR / Instrumento de adiantamento + CDB em garantia', icon: '♻️',
        description: 'Cash collateral (nome interno) = CASHCOL (comercial). CPR / Instrumento de adiantamento com CDB em garantia. 1 fatura por parcela da CPR.',
        resultingAssetType: 'LOAN', status: 'ACTIVE', currency: 'BRL', isNew: true,
        minRate: 1.1, maxRate: 2.8, maxAmount: 50000000, maxTerm: 540,
        guarantees: [{ type: 'CDB_VINCULADO', requirement: 'MANDATORY', retentionPercentage: 100 }],
        eligibilityCriteria: [
            { criteriaId: 'ec-cash-1', participantTypeId: 1, minRating: 'AA', maxRating: 'D' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-cash-1', participantTypeId: 1, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-cash-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'CPR_CDB' },
        ],
        operationStructure: {
            instrumentType: 'ADVANCE_INSTRUMENT_CDB', discountedReceivable: 'NONE', receivableRecourse: 'NA',
            mandatoryGuaranteeLabel: 'CDB', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_CPR_INSTALLMENT', isLcaBallast: false,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: false, collateralTypeCode: '1', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-002',
        createdBy: 'jean.dobre', createdAt: '2026-05-20T10:00:00Z',
    },
    {
        productId: 'bp-order', commercialName: 'ORDER', shortCode: 'ORDER',
        fullName: 'Cessão de pedido', icon: '📋',
        description: 'Cessão de pedido. No BO: sempre uma CPR clean com pedidos em anexo. Regresso é booleana (com/sem). Valor total = somatória dos Direitos Creditórios (pedidos).',
        resultingAssetType: 'LOAN', status: 'ACTIVE', currency: 'BRL', isNew: true,
        minRate: 1.2, maxRate: 3.0, maxAmount: 40000000, maxTerm: 360,
        guarantees: [],
        eligibilityCriteria: [
            { criteriaId: 'ec-order-1', participantTypeId: 1, minRating: 'AA', maxRating: 'C' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-order-1', participantTypeId: 1, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-order-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'CPR_CLEAN_ORDERS' },
        ],
        operationStructure: {
            instrumentType: 'ORDER_ASSIGNMENT', discountedReceivable: 'ORDER', receivableRecourse: 'NO',
            mandatoryGuaranteeLabel: 'N/A', guaranteeRecourse: 'NA', invoiceGranularity: 'CPR_CLEAN_WITH_ORDERS', isLcaBallast: false,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: true, collateralTypeCode: '2', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-002',
        createdBy: 'jean.dobre', createdAt: '2026-06-10T10:00:00Z',
    },
    {
        productId: 'bp-clean', commercialName: 'Clean', shortCode: 'CLEAN',
        fullName: 'CPR sem garantia (Forfaiting)', icon: '💸',
        description: 'CPR pura, sem garantia. Comercialmente próxima do Forfaiting. 1 fatura por parcela da CPR.',
        resultingAssetType: 'LOAN', status: 'ACTIVE', currency: 'BRL',
        minRate: 1.4, maxRate: 3.5, maxAmount: 40000000, maxTerm: 360,
        guarantees: [],
        eligibilityCriteria: [
            { criteriaId: 'ec-clean-1', participantTypeId: 1, minRating: 'AA', maxRating: 'B' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-clean-1', participantTypeId: 1, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-clean-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'CPR_F' },
        ],
        operationStructure: {
            instrumentType: 'CPR', discountedReceivable: 'NONE', receivableRecourse: 'NA',
            mandatoryGuaranteeLabel: 'N/A', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_CPR_INSTALLMENT', isLcaBallast: false,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: true, collateralTypeCode: '2', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-002',
        createdBy: 'jean.dobre', createdAt: '2026-01-20T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-01-22T09:00:00Z',
    },
    {
        productId: 'bp-base', commercialName: 'Base', shortCode: 'BASE',
        fullName: 'CPR com ao menos uma garantia', icon: '🏛️',
        description: 'CPR com ao menos 1 garantia de qualquer tipo. 1 fatura por parcela da CPR.',
        resultingAssetType: 'LOAN', status: 'ACTIVE', currency: 'BRL',
        minRate: 1.3, maxRate: 3.2, maxAmount: 60000000, maxTerm: 540,
        guarantees: [{ type: 'AVAL_PROPRIO', requirement: 'MANDATORY' }],
        eligibilityCriteria: [
            { criteriaId: 'ec-base-1', participantTypeId: 1, minRating: 'AA', maxRating: 'C' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-base-1', participantTypeId: 1, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-base-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'CPR_F' },
        ],
        operationStructure: {
            instrumentType: 'CPR', discountedReceivable: 'NONE', receivableRecourse: 'NA',
            mandatoryGuaranteeLabel: 'Ao menos 1, de qualquer tipo', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_CPR_INSTALLMENT', isLcaBallast: false,
        },
        b3Config: { b3Product: 'CPR_INSTRUMENT', registersGuarantee: true, collateralTypeCode: '2', operationModalityCode: '0215', finalityCode: '6096' },
        dimensaRegistrationProfileId: 'dmprof-002',
        createdBy: 'jean.dobre', createdAt: '2026-02-01T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-02-03T09:00:00Z',
    },
    {
        productId: 'bp-prime', commercialName: 'PRIME', shortCode: 'PRIME',
        fullName: 'KEEP com garantia obrigatória (Bônus de Adimplência)', icon: '💰',
        description: 'É um KEEP com garantia obrigatória: cessão de recebíveis, N sacados, sem regresso do recebível, garantia CDB ou Bônus Adimplência, com regresso da garantia (do CDB, proporcional ao inadimplemento). 1 fatura por recebível.',
        resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'ACTIVE', currency: 'BRL',
        minRate: 0.8, maxRate: 2.0, maxAmount: 100000000, maxTerm: 720,
        guarantees: [{ type: 'CDB_VINCULADO', requirement: 'MANDATORY', retentionPercentage: 100 }],
        eligibilityCriteria: [
            { criteriaId: 'ec-prime-1', participantTypeId: 3, minRating: 'AA', maxRating: 'B' },
            { criteriaId: 'ec-prime-2', participantTypeId: 4, minRating: 'AA', maxRating: 'C' },
        ],
        allowedParticipants: [
            { allowedParticipantId: 'adp-prime-1', participantTypeId: 3, isRequired: true, requiredDocuments: [
                { documentType: 'CONTRACT_SOCIAL', isRequired: true },
                { documentType: 'BALANCE_SHEET', isRequired: true },
            ] },
            { allowedParticipantId: 'adp-prime-2', participantTypeId: 4, isRequired: true, requiredDocuments: [
                { documentType: 'CPF_CNPJ_CARD', isRequired: true },
            ] },
        ],
        instrumentBindings: [
            { bindingId: 'tb-prime-1', stage: 'CONTRACT_GENERATION', instrumentCode: 'ASSIGNMENT_BONUS' },
        ],
        operationStructure: {
            instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'NO',
            mandatoryGuaranteeLabel: 'CDB ou Bônus Adimplência', guaranteeRecourse: 'YES', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: true,
        },
        b3Config: { b3Product: 'CREDIT_RIGHT', registersGuarantee: false, operationModalityCode: '', finalityCode: '' },
        dimensaRegistrationProfileId: 'dmprof-001',
        createdBy: 'jean.dobre', createdAt: '2026-06-05T10:00:00Z',
        activatedBy: 'jean.dobre', activatedAt: '2026-06-08T09:00:00Z',
    },
];

// ===================== helpers =====================

export const getParticipantTypeLabel = (id: ParticipantTypeId): string =>
    PARTICIPANT_TYPES.find(p => p.id === id)?.label || String(id);

export const getGuaranteeTypeLabel = (type: GuaranteeType): string =>
    GUARANTEE_TYPES.find(g => g.value === type)?.label || type;

export const getRatingRangeLabel = (min: RatingGrade | null, max: RatingGrade | null): string => {
    if (!min && !max) return 'Todos';
    if (min === max) return min || '';
    return `${min || RATING_ORDER[0]} — ${max || RATING_ORDER[RATING_ORDER.length - 1]}`;
};

export const createEmptyBankingProduct = (): BankingProduct => ({
    productId: `bp-${Date.now()}`,
    commercialName: '', shortCode: '', fullName: '', icon: '📦', description: '',
    resultingAssetType: 'RECEIVABLE_ASSIGNMENT', status: 'DRAFT', currency: 'BRL',
    minRate: 0, maxRate: 0, maxAmount: 0, maxTerm: 360,
    guarantees: [], eligibilityCriteria: [], allowedParticipants: [], instrumentBindings: [],
    operationStructure: {
        instrumentType: 'RECEIVABLES_ASSIGNMENT', discountedReceivable: 'NF', receivableRecourse: 'NO',
        mandatoryGuaranteeLabel: 'N/A', guaranteeRecourse: 'NA', invoiceGranularity: 'PER_RECEIVABLE', isLcaBallast: false,
    },
    b3Config: null,
    dimensaRegistrationProfileId: null,
    createdBy: 'current.user', createdAt: new Date().toISOString(),
});
